import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inviteApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const branding = useBranding();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '', phone: '' });

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await inviteApi.validate(token);
        setInvite(res.data);
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) setError('This invite link has expired. Please contact your administrator for a new one.');
        else if (status === 404) setError('This invite link is invalid or has already been used.');
        else setError('Unable to validate invite. Please try again.');
      } finally { setLoading(false); }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (!form.name.trim()) { setError('Name is required'); return; }

    setSubmitting(true);
    try {
      await inviteApi.accept({ token, name: form.name.trim(), password: form.password, phone: form.phone });
      setSuccess(true);
      await checkAuth();
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--background)' }}>
      <div className="w-full max-w-md" data-testid="invite-accept-page">
        {/* Logo */}
        <div className="text-center mb-6">
          {branding.logo_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`} alt="ProjectBook Pro™"
              className="w-full max-w-[200px] mx-auto rounded-xl object-contain logo-bg p-2 mb-3" />
          ) : (
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-heading)' }}>{branding.app_name}</h1>
          )}
        </div>

        <div className="rounded-xl shadow-lg p-6" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)' }}>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Validating your invite...</p>
            </div>
          ) : error && !invite ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(255,51,51,0.1)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF3333" viewBox="0 0 256 256"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm-8,56a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm8,104a12,12,0,1,1,12-12A12,12,0,0,1,128,184Z"/></svg>
              </div>
              <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-heading)' }}>Invite Not Valid</h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>{error}</p>
              <button onClick={() => navigate('/login')} className="text-sm font-medium underline" style={{ color: 'var(--primary)' }}>Go to Login</button>
            </div>
          ) : success ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(0,200,83,0.1)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#00C853" viewBox="0 0 256 256"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg>
              </div>
              <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-heading)' }}>Account Created!</h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Welcome aboard, {form.name}. Redirecting to dashboard...</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-5">
                <h2 className="text-xl font-bold" style={{ color: 'var(--text-heading)' }}>Set Up Your Account</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  {invite?.invited_by_name ? `${invite.invited_by_name} has invited you` : "You've been invited"} to join as <strong style={{ color: 'var(--primary)' }}>{invite?.role}</strong>
                </p>
                {invite?.message && (
                  <p className="text-xs mt-2 px-3 py-2 rounded-lg italic" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-secondary)' }}>"{invite.message}"</p>
                )}
              </div>

              {error && <div className="p-2.5 rounded-lg text-sm mb-4" style={{ backgroundColor: 'rgba(255,51,51,0.1)', color: '#FF3333' }}>{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Email</label>
                  <input type="email" value={invite?.email || ''} disabled
                    className="w-full px-3 py-2.5 rounded-lg border text-sm opacity-60"
                    style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--input-border)', color: 'var(--foreground)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Full Name</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                    className="w-full px-3 py-2.5 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--foreground)' }}
                    placeholder="Enter your full name" data-testid="invite-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Phone (optional)</label>
                  <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--foreground)' }}
                    placeholder="+971 XX XXX XXXX" data-testid="invite-phone-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Password</label>
                  <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required
                    className="w-full px-3 py-2.5 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--foreground)' }}
                    placeholder="At least 6 characters" data-testid="invite-password-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Confirm Password</label>
                  <input type="password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} required
                    className="w-full px-3 py-2.5 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--foreground)' }}
                    placeholder="Re-enter password" data-testid="invite-confirm-input" />
                </div>
                <button type="submit" disabled={submitting}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: 'var(--primary)' }} data-testid="invite-submit-btn">
                  {submitting ? 'Creating Account...' : 'Create Account & Sign In'}
                </button>
              </form>

              <p className="text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                Already have an account? <button onClick={() => navigate('/login')} className="font-medium underline" style={{ color: 'var(--primary)' }}>Sign in</button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
